import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import {
  getSigningInfo,
  sendOtp,
  verifyOtp,
} from '../../services/signingService';
import '../../assets/scss/components/_verify-access.scss';
import { formatDateTimeUTC } from '../../utils/dateFormat';

const OTP_LENGTH = 6;
const OTP_DEFAULT_EXPIRY_MINUTES = 10;
const OTP_EXPIRES_STORAGE_PREFIX = 'signing_otp_expires_at:';

const getStoredOtpExpiresAt = (id) => {
  if (!id) return 0;
  try {
    const raw = localStorage.getItem(`${OTP_EXPIRES_STORAGE_PREFIX}${id}`);
    const ts = parseInt(raw, 10);
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
};

const setStoredOtpExpiresAt = (id, expiresAtMs) => {
  if (!id) return;
  try {
    localStorage.setItem(`${OTP_EXPIRES_STORAGE_PREFIX}${id}`, String(expiresAtMs));
  } catch {
    // ignore quota / private mode
  }
};

const clearStoredOtpExpiresAt = (id) => {
  if (!id) return;
  try {
    localStorage.removeItem(`${OTP_EXPIRES_STORAGE_PREFIX}${id}`);
  } catch {
    // ignore
  }
};

const VerifyAccess = () => {
  const { recipientId } = useParams();
  const navigate = useNavigate();

  // Page states
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Info from signing-info endpoint
  const [signingInfo, setSigningInfo] = useState(null);

  // OTP state
  const [otpValues, setOtpValues] = useState(Array(OTP_LENGTH).fill(''));
  const [otpSent, setOtpSent] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  // Refs for OTP inputs
  const inputRefs = useRef([]);
  const timerRef = useRef(null);
  const otpExpiryRef = useRef(0);

  // Start countdown timer (persisted so remount / Gmail WebView return won't resend)
  const startTimer = useCallback((seconds, { persist = true } = {}) => {
    const safeSeconds = Math.max(0, Math.round(Number(seconds) || 0));
    const expiresAt = Date.now() + safeSeconds * 1000;
    otpExpiryRef.current = expiresAt;
    setTimeLeft(safeSeconds);

    if (persist && recipientId && safeSeconds > 0) {
      setStoredOtpExpiresAt(recipientId, expiresAt);
    }

    if (timerRef.current) clearInterval(timerRef.current);
    if (safeSeconds <= 0) {
      if (recipientId) clearStoredOtpExpiresAt(recipientId);
      return;
    }

    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.round((otpExpiryRef.current - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        if (recipientId) clearStoredOtpExpiresAt(recipientId);
      }
    }, 1000);
  }, [recipientId]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // If signing page redirected here due to expired/invalid token, show that reason.
  useEffect(() => {
    const redirectedAuthError = sessionStorage.getItem('signing_auth_error');
    if (redirectedAuthError) {
      setError(String(redirectedAuthError));
      sessionStorage.removeItem('signing_auth_error');
    }
  }, []);

  // ─── Init: fetch signing info & send OTP (or resume cooldown) ─────────────
  useEffect(() => {
    const init = async () => {
      if (!recipientId) {
        setError('Invalid signing link. No recipient ID provided.');
        setLoading(false);
        return;
      }

      try {
        // Step 1: Get signing info
        const infoResponse = await getSigningInfo(recipientId);
        const info = infoResponse?.data || infoResponse;
        setSigningInfo(info);

        // Check if already signed or declined
        if (info.already_signed) {
          setError('This document has already been signed.');
          setLoading(false);
          return;
        }
        if (info.already_declined) {
          setError('This document has been declined.');
          setLoading(false);
          return;
        }

        // If OTP was already verified recently, skip to signing page
        if (info.otp_verified) {
          // OTP still valid — but we need a signing_token.
          // The backend doesn't give us one from signing-info,
          // so we still need the user to go through OTP or show
          // a "continue" option.  For now, just send a fresh OTP.
        }

        // Step 2: Resume existing cooldown, or send OTP once
        const storedExpiresAt = getStoredOtpExpiresAt(recipientId);
        const remainingSec = Math.max(
          0,
          Math.round((storedExpiresAt - Date.now()) / 1000)
        );

        if (remainingSec > 0) {
          setOtpSent(true);
          setSuccess('OTP has already been sent to your email. Please enter that code.');
          startTimer(remainingSec, { persist: false });
        } else {
          const otpResponse = await sendOtp(recipientId);
          setOtpSent(true);
          setSuccess(otpResponse?.message || 'OTP has been sent to your email address');
          const expiryMinutes = otpResponse?.expires_in || OTP_DEFAULT_EXPIRY_MINUTES;
          startTimer(expiryMinutes * 60, { persist: true });
        }
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'This signing link is invalid or has expired.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [recipientId, startTimer]);

  // Format seconds as M:SS
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ─── OTP input handlers ───────────────────────────────────────────────────

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);

    const newValues = [...otpValues];
    newValues[index] = digit;
    setOtpValues(newValues);

    // Auto-focus next
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;

    const newValues = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((char, i) => {
      newValues[i] = char;
    });
    setOtpValues(newValues);

    const nextEmpty = newValues.findIndex((v) => !v);
    const focusIdx = nextEmpty === -1 ? OTP_LENGTH - 1 : nextEmpty;
    inputRefs.current[focusIdx]?.focus();
  };

  // ─── Verify OTP ───────────────────────────────────────────────────────────

  const handleVerify = async (e) => {
    if (e) e.preventDefault();

    const otp = otpValues.join('');
    if (otp.length !== OTP_LENGTH) {
      setError('Please enter the complete 6-digit OTP code.');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      const result = await verifyOtp(recipientId, otp);
      const sessionData = result?.data || result;

      // Store signing session for the signing document page
      sessionStorage.setItem('signing_session', JSON.stringify(sessionData));
      sessionStorage.setItem('signing_token', sessionData.signing_token || '');
      sessionStorage.setItem('signing_recipient_id', recipientId);
      clearStoredOtpExpiresAt(recipientId);

      // Navigate to signing document page
      navigate(`/sign/${recipientId}/document`, { replace: true });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Invalid OTP. Please try again.';
      setError(message);
      // Clear inputs
      setOtpValues(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  // Auto-submit when all digits filled
  useEffect(() => {
    if (otpValues.every((v) => v) && !verifying) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpValues]);

  // ─── Resend OTP ───────────────────────────────────────────────────────────

  const handleResend = async () => {
    if (resending || timeLeft > 0) return;

    setResending(true);
    setError('');
    setSuccess('');

    try {
      const otpResponse = await sendOtp(recipientId);
      setOtpSent(true);
      setSuccess(otpResponse?.message || 'OTP has been sent to your email address');
      setOtpValues(Array(OTP_LENGTH).fill(''));
      const expiryMinutes = otpResponse?.expires_in || OTP_DEFAULT_EXPIRY_MINUTES;
      startTimer(expiryMinutes * 60, { persist: true });
      inputRefs.current[0]?.focus();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to resend OTP. Please try again.';
      setError(message);
    } finally {
      setResending(false);
    }
  };

  // ─── Derived info for display ─────────────────────────────────────────────

  const recipient = signingInfo?.recipient;
  const envelopeSubject =
    signingInfo?.envelope?.subject ||
    signingInfo?.subject ||
    signingInfo?.envelope_subject ||
    '-';
  const recipientName = recipient?.name || '-';
  const recipientEmail = recipient?.email || '-';
  const recipientRole = recipient?.type === 'signee'
    ? 'Signee'
    : recipient?.type === 'review_recipient'
      ? 'Reviewer'
      : recipient?.type === 'copy_recipient'
        ? 'Copy Recipient'
        : recipient?.type || 'Signee';

  // ─── Render: loading ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="verify-access-page">
        <div className="verify-card">
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Verifying your access...</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: already signed ───────────────────────────────────────────────

  if (signingInfo?.already_signed) {
    const signedAt = signingInfo?.signed_at
      ? formatDateTimeUTC(signingInfo.signed_at)
      : null;

    return (
      <div className="status-screen status-screen--signed">
        <div className="status-card">
          <div className="status-icon-circle status-icon--info">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="24" fill="#dbeafe" />
              <circle cx="24" cy="24" r="16" stroke="#3b82f6" strokeWidth="2.5" fill="none" />
              <circle cx="24" cy="18" r="2" fill="#3b82f6" />
              <rect x="22.5" y="22" width="3" height="10" rx="1.5" fill="#3b82f6" />
            </svg>
          </div>
          <h1 className="status-title">Already Signed</h1>
          <p className="status-subtitle">
            You have already signed this document. No further action is required. A completed copy will be sent to you once all parties have signed.
          </p>
          <div className="status-details">
            <div className="status-detail-row">
              <span className="status-label">Status</span>
              <span className="status-value status-val--signed">&#10003; Previously Signed</span>
            </div>
            {signedAt && (
              <div className="status-detail-row">
                <span className="status-label">Signed At</span>
                <span className="status-value">{signedAt}</span>
              </div>
            )}
            <div className="status-detail-row">
              <span className="status-label">Subject</span>
              <span className="status-value">{envelopeSubject}</span>
            </div>
          </div>
          <p className="status-footer">Powered by <strong>VolkSign</strong> &mdash; Secure Digital Signatures</p>
        </div>
      </div>
    );
  }

  // ─── Render: already declined ─────────────────────────────────────────────

  if (signingInfo?.already_declined) {
    const declinedAt = signingInfo?.declined_at
      ? formatDateTimeUTC(signingInfo.declined_at)
      : null;

    return (
      <div className="status-screen status-screen--declined">
        <div className="status-card">
          <div className="status-icon-circle status-icon--declined">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="24" fill="#fee2e2" />
              <path d="M17 17L31 31M31 17L17 31" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </div>
          <h1 className="status-title">Document Declined</h1>
          <p className="status-subtitle">
            You have declined to sign this document. The sender has been notified of your decision.
          </p>
          {signingInfo?.decline_reason && (
            <div className="status-reason-box">
              <span className="status-reason-label">REASON</span>
              <p className="status-reason-text">{signingInfo.decline_reason}</p>
            </div>
          )}
          <div className="status-details">
            <div className="status-detail-row">
              <span className="status-label">Status</span>
              <span className="status-value status-val--declined">&#10007; Declined</span>
            </div>
            {declinedAt && (
              <div className="status-detail-row">
                <span className="status-label">Date</span>
                <span className="status-value">{declinedAt}</span>
              </div>
            )}
            <div className="status-detail-row">
              <span className="status-label">Subject</span>
              <span className="status-value">{envelopeSubject}</span>
            </div>
          </div>
          <p className="status-footer">Powered by <strong>VolkSign</strong> &mdash; Secure Digital Signatures</p>
        </div>
      </div>
    );
  }

  // ─── Render: fatal error (invalid link, no info) ──────────────────────────

  if (!signingInfo && error) {
    return (
      <div className="verify-access-page">
        <div className="verify-card">
          <div className="verify-icon">
            <span role="img" aria-label="error">&#10060;</span>
          </div>
          <h1 className="verify-title">Access Denied</h1>
          <p className="verify-subtitle text-danger">{error}</p>
        </div>
      </div>
    );
  }

  // ─── Render: OTP verification form ────────────────────────────────────────

  return (
    <div className="verify-access-page">
      <div className="verify-card">
        {/* Lock icon */}
        <div className="verify-icon">
          <span role="img" aria-label="lock">&#128274;</span>
        </div>

        {/* Title */}
        <h1 className="verify-title">Verify Your Access</h1>
        <p className="verify-subtitle">Secure document access verification</p>

        {/* Document info table */}
        <div className="verify-info-table">
          <div className="info-row">
            <span className="info-label">Subject:</span>
            <span className="info-value">{envelopeSubject}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Recipient:</span>
            <span className="info-value">{recipientName}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Email:</span>
            <span className="info-value">{recipientEmail}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Role:</span>
            <span className="info-value">{recipientRole}</span>
          </div>
        </div>

        {/* Success banner */}
        {otpSent && success && (
          <div className="otp-success-banner">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="check-icon">
              <rect width="16" height="16" rx="3" fill="#28a745" />
              <path d="M4.5 8L7 10.5L11.5 5.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{success}</span>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="otp-error-banner">
            <span>{error}</span>
          </div>
        )}

        {/* OTP Inputs */}
        <form onSubmit={handleVerify} className="otp-form">
          <div className="otp-inputs" onPaste={handlePaste}>
            {otpValues.map((val, idx) => (
              <input
                key={idx}
                ref={(el) => (inputRefs.current[idx] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className={`otp-input ${val ? 'filled' : ''}`}
                value={val}
                onChange={(e) => handleOtpChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                autoFocus={idx === 0}
                disabled={verifying}
              />
            ))}
          </div>

          {/* Timer */}
          {timeLeft > 0 && (
            <div className="otp-timer">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="timer-icon">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>OTP expires in: {formatTime(timeLeft)}</span>
            </div>
          )}

          {/* Verify button */}
          <button
            type="submit"
            className="verify-btn"
            disabled={verifying || otpValues.some((v) => !v)}
          >
            {verifying ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Verifying...
              </>
            ) : (
              'Verify & Access Document'
            )}
          </button>
        </form>

        {/* Resend */}
        <div className="resend-section">
          <p className="resend-text">Didn&apos;t receive the code?</p>
          <button
            type="button"
            className="resend-btn"
            onClick={handleResend}
            disabled={resending || timeLeft > 0}
          >
            {resending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending...
              </>
            ) : timeLeft > 0 ? (
              `Resend OTP in ${formatTime(timeLeft)}`
            ) : (
              'Resend OTP'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyAccess;
